import React, { useState, useEffect, useRef } from "react";
import Webcam from "react-webcam";
import { drawMesh } from "../utils/drawMesh";
import * as tf from "@tensorflow/tfjs";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Auth, DataStore } from "aws-amplify";
import {  DatePicker, Select } from "../components/ui";
import { Vitals } from "../models";

import {
    Button,
    Card,
    Flex,
    Grid,
    SelectField,
    TextField,
  } from "@aws-amplify/ui-react";

import '../App.css';

const FaceScan = () => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const scanInterval = useRef(null);
  const modelRef = useRef(null);

  const [vitals, setVitals] = useState({ hr: 0, spo2: 0, rr: 0, bp: 0 });
  const [isScanning, setIsScanning] = useState(false);
  const [scanTime, setScanTime] = useState(0);
  const [webcamEnabled, setWebcamEnabled] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const [recordCount, setRecordCount] = useState(0);
  const maxRecords = 15; 

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        setUserEmail(user.attributes.email);
      })
      .catch((err) => console.error("Auth error:", err));
  }, []);

  const requestCameraPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
    //  console.log("✅ Camera permission granted");
    } catch (error) {
      console.error("❌ Camera permission denied:", error);
      alert("Please allow camera access in your browser settings.");
    }
  };

  const loadFaceMeshModel = async () => {
    try {
      setWebcamEnabled(true);
      await requestCameraPermission();

      const model = await faceLandmarksDetection.createDetector(
        faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh,
        { runtime: "tfjs" }
      );

      modelRef.current = model;
    //  console.log("✅ FaceMesh model loaded!");
      startFaceScan();
    } catch (error) {
      console.error("❌ Error loading FaceMesh model:", error);
    }
  };

  const startFaceScan = async () => {
   // console.log("@startFaceScan userEmail:", userEmail);
    if (!webcamRef.current || !webcamRef.current.video) return;

    const video = webcamRef.current.video;
    if (!video || video.readyState !== 4) {
      console.log("⌛ Waiting for webcam to load...");
      setTimeout(startFaceScan, 500);
      return;
    }

  //  video.width = 480;
   // video.height = 360;
    video.width = Math.min(window.innerWidth * 0.9, 480);  // 90% of screen width, max 480px
    video.height = Math.min(window.innerHeight * 0.6, 360);  // 60% of screen height, max 360px

    setIsScanning(true);
    setScanTime(0);
    setRecordCount(0);

    scanInterval.current = setInterval(async () => {
      if (recordCount >= maxRecords) {
        console.log("✅ Max records reached. Stopping scan.");
        stopFaceScan();
        return;
      }

      if (!modelRef.current || !webcamRef.current.video) return;

      try {
        const faces = await modelRef.current.estimateFaces(video, false);

        if (!faces || faces.length === 0) {
          console.warn("⚠️ No face detected, skipping this frame.");
          return;
        }

        const face = faces[0]; // Get first face
        if (!face) {
          console.warn("⚠️ No valid face data found.");
          return;
        }

        // Draw face mesh
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext("2d");
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          drawMesh(faces, ctx);
        }

        // Simulate vitals
        const newVitals = {
          hr: Math.round(Math.random() * (100 - 60) + 60),
          spo2: Math.round(Math.random() * (100 - 95) + 95),
          rr: Math.round(Math.random() * (20 - 12) + 12),
          bp: Math.round(Math.random() * (120 - 90) + 90),
        };

        setVitals(newVitals);
        await saveVitalsToDB(newVitals.hr, newVitals.spo2, newVitals.rr, newVitals.bp);
      } catch (error) {
        console.error("❌ Error in face scan:", error);
      }
    }, 1000);
  };

  const saveVitalsToDB = async (hr, spo2, rr, bp) => {
    if (!userEmail) {
      console.error("❌ Error: userEmail is undefined, skipping save.");
      return;
    }

    setRecordCount((prevCount) => {
      if (prevCount >= maxRecords) {
        stopFaceScan();
        return prevCount;
      }

      const vitalRecord = new Vitals({
        userEmail,
        hr,
        spo2,
        rr,
        bp,
        timestamp: new Date().toISOString(),
      });

      DataStore.save(vitalRecord)
        .then(() => console.log(`✅ Vitals saved! Record ${prevCount + 1}/${maxRecords}`))
        .catch((error) => console.error("❌ Error saving vitals:", error));

      return prevCount + 1;
    });
  };

  const stopFaceScan = () => {
    clearInterval(scanInterval.current);
    setIsScanning(false);
    setWebcamEnabled(false);
  //  console.log("⏹️ Scan stopped.");
    setTimeout(() => setRecordCount(0), 500);
  };

  return (
    <div className="flex flex-col items-center">
      <Card className="p-4">
        <div className="mt-4">
          <h2 className="text-xl font-semibold">Face Scan</h2>
          <p>{isScanning ? `⏳ Scanning... ${scanTime}s` : "Not scanning"}</p>
        </div>

        {webcamEnabled && (
          <div className="relative">
            <Webcam ref={webcamRef} className="rounded-xl shadow-md" />
            <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full" />
          </div>
        )}

        <div className="mt-4 bg-white p-4 rounded-2xl shadow-md w-full max-w-sm">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-3">Vitals</h2>
          <div className="grid grid-cols-2 gap-4 text-gray-700">
            <div className="flex items-center gap-2">
              
              <p className="text-lg font-medium">HR <span className="text-red-500 text-xl">❤️</span> : <span className="font-semibold">{vitals.hr} bpm</span></p>
            </div>
            <div className="flex items-center gap-2">
              
              <p className="text-lg font-medium">RR <span className="text-blue-500 text-xl">🫁</span> :  <span className="font-semibold">{vitals.rr} bpm</span></p>
            </div>
            <div className="flex items-center gap-2">
              
              <p className="text-lg font-medium">SpO2 <span className="text-green-500 text-xl">🩸</span> : <span className="font-semibold">{vitals.spo2}%</span></p>
            </div>
            <div className="flex items-center gap-2">
              
              <p className="text-lg font-medium">BP <span className="text-purple-500 text-xl">💓</span> : <span className="font-semibold">{vitals.bp} mmHg</span></p>
            </div>
          </div>
        </div>


        <div className="flex gap-4 mt-4">
          <Button onClick={() => loadFaceMeshModel()} disabled={isScanning} className="start-scan-btn">
            Start Scan
          </Button>
          <span> &nbsp;  &nbsp;  &nbsp; </span>
          <Button onClick={stopFaceScan} disabled={!isScanning} className="stop-scan-btn">
            Stop Scan
          </Button>
          </div>
      </Card>
    </div>
  );
};

export default FaceScan;